/**
 * FFmpeg CDN 配置文件
 * 可以根据需要添加、删除或修改 CDN 源
 */

export interface CDNConfig {
  name: string;
  coreURL: string;
  wasmURL: string;
  priority?: number; // 优先级，数字越小优先级越高
  region?: string;   // 地区标识
  description?: string;
}

/**
 * 默认 CDN 源配置
 * 按推荐优先级排序
 */
export const DEFAULT_CDN_SOURCES: CDNConfig[] = [
  {
    name: 'unpkg',
    coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.js',
    wasmURL: 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm',
    priority: 1,
    region: 'global',
    description: 'unpkg CDN - 全球分发'
  },
  {
    name: 'jsdelivr',
    coreURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.js',
    wasmURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm',
    priority: 2,
    region: 'global',
    description: 'jsDelivr CDN - 全球分发'
  },
  {
    name: 'jsdelivr-fastly',
    coreURL: 'https://fastly.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.js',
    wasmURL: 'https://fastly.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm',
    priority: 3,
    region: 'global',
    description: 'jsDelivr Fastly CDN - 高速节点'
  }
];

/**
 * 中国大陆优化的 CDN 源
 */
export const CHINA_CDN_SOURCES: CDNConfig[] = [
  {
    name: 'jsdelivr-gcore',
    coreURL: 'https://gcore.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.js',
    wasmURL: 'https://gcore.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm/ffmpeg-core.wasm',
    priority: 1,
    region: 'china',
    description: 'jsDelivr GCore CDN - 中国优化'
  }
];

/**
 * 自定义 CDN 源示例
 * 注意：请确保自定义CDN源上的文件存在且可访问
 */
export const CUSTOM_CDN_SOURCES: CDNConfig[] = [
  // 阿里云OSS - 最高优先级
  {
    name: 'aliyun-oss',
    coreURL: 'https://ruiii-app.oss-cn-hangzhou.aliyuncs.com/public/ffmpeg/ffmpeg-core.js',
    wasmURL: 'https://ruiii-app.oss-cn-hangzhou.aliyuncs.com/public/ffmpeg/ffmpeg-core.wasm',
    priority: 0, // 最高优先级
    region: 'china',
    description: '阿里云OSS - 中国优化（优先）'
  }
  // 更多自定义源...
];

/**
 * CDN 配置管理器
 */
export class CDNConfigManager {
  private static instance: CDNConfigManager;
  private cdnSources: CDNConfig[] = [];

  private constructor() {
    this.loadDefaultConfig();
  }

  static getInstance(): CDNConfigManager {
    if (!CDNConfigManager.instance) {
      CDNConfigManager.instance = new CDNConfigManager();
    }
    return CDNConfigManager.instance;
  }

  /**
   * 加载默认配置
   */
  private loadDefaultConfig(): void {
    // 优先使用自定义服务器
    this.cdnSources = [...CUSTOM_CDN_SOURCES];

    // 根据地区自动选择 CDN 源
    const isChina = this.detectChineseRegion();

    if (isChina) {
      this.cdnSources.push(...CHINA_CDN_SOURCES, ...DEFAULT_CDN_SOURCES);
    } else {
      this.cdnSources.push(...DEFAULT_CDN_SOURCES);
    }

    // 按优先级排序
    this.cdnSources.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  }

  /**
   * 检测是否为中国地区
   */
  private detectChineseRegion(): boolean {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;

    return timezone.includes('Shanghai') ||
      timezone.includes('Beijing') ||
      language.includes('zh-CN');
  }

  /**
   * 获取所有 CDN 源
   */
  getAllSources(): CDNConfig[] {
    return [...this.cdnSources];
  }

  /**
   * 根据地区获取 CDN 源
   */
  getSourcesByRegion(region: string): CDNConfig[] {
    return this.cdnSources.filter(source =>
      source.region === region || source.region === 'global'
    );
  }

  /**
   * 添加自定义 CDN 源
   */
  addCustomSource(source: CDNConfig): void {
    this.cdnSources.push(source);
    this.cdnSources.sort((a, b) => (a.priority || 999) - (b.priority || 999));
  }

  /**
   * 移除 CDN 源
   */
  removeSource(name: string): void {
    this.cdnSources = this.cdnSources.filter(source => source.name !== name);
  }

  /**
   * 更新 CDN 源优先级
   */
  updatePriority(name: string, priority: number): void {
    const source = this.cdnSources.find(s => s.name === name);
    if (source) {
      source.priority = priority;
      this.cdnSources.sort((a, b) => (a.priority || 999) - (b.priority || 999));
    }
  }

  /**
   * 获取推荐的 CDN 源（前3个）
   */
  getRecommendedSources(): CDNConfig[] {
    return this.cdnSources.slice(0, 3);
  }
}

/**
 * 导出配置管理器实例
 */
export const cdnConfig = CDNConfigManager.getInstance();
